/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CreatePersonioAppRequest } from '../models/CreatePersonioAppRequest';
import type { PaginatedPaychexPayrollSyncList } from '../models/PaginatedPaychexPayrollSyncList';
import type { PaginatedPersonioList } from '../models/PaginatedPersonioList';
import type { PaginatedPersonioSyncList } from '../models/PaginatedPersonioSyncList';
import type { PatchedPaychexAPICredentialsUpdateRequest } from '../models/PatchedPaychexAPICredentialsUpdateRequest';
import type { PaychexAPICredentials } from '../models/PaychexAPICredentials';
import type { PaychexAPICredentialsCreateRequest } from '../models/PaychexAPICredentialsCreateRequest';
import type { PaychexAPICredentialsUpdate } from '../models/PaychexAPICredentialsUpdate';
import type { PaychexCompaniesResponse } from '../models/PaychexCompaniesResponse';
import type { PaychexPayrollSyncDetail } from '../models/PaychexPayrollSyncDetail';
import type { PayrollSyncStatus } from '../models/PayrollSyncStatus';
import type { Personio } from '../models/Personio';
import type { PersonioCompensationTypeSuccessResponse } from '../models/PersonioCompensationTypeSuccessResponse';
import type { PersonioSyncDetail } from '../models/PersonioSyncDetail';
import type { TriggerPayrollSyncRequest } from '../models/TriggerPayrollSyncRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class IntegrationsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaychexAPICredentials
     * @throws ApiError
     */
    public companiesPaychexCreate({
        parentCompany,
        requestBody,
    }: {
        parentCompany: string,
        requestBody: PaychexAPICredentialsCreateRequest,
    }): CancelablePromise<PaychexAPICredentials> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/paychex/',
            path: {
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaychexAPICredentialsUpdate
     * @throws ApiError
     */
    public companiesPaychexPartialUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        /**
         * A unique integer value identifying this Paychex API Credentials.
         */
        id: number,
        parentCompany: string,
        requestBody?: PatchedPaychexAPICredentialsUpdateRequest,
    }): CancelablePromise<PaychexAPICredentialsUpdate> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{parent_company}/paychex/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaychexCompaniesResponse
     * @throws ApiError
     */
    public companiesPaychexCompaniesRetrieve({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<PaychexCompaniesResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/paychex/companies/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaychexAPICredentials
     * @throws ApiError
     */
    public companiesPaychexCredentialsRetrieve({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<PaychexAPICredentials> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/paychex/credentials/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedPaychexPayrollSyncList
     * @throws ApiError
     */
    public companiesPaychexSyncList({
        parentCompany,
        limit,
        offset,
    }: {
        parentCompany: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedPaychexPayrollSyncList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/paychex/sync/',
            path: {
                'parent_company': parentCompany,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaychexPayrollSyncDetail
     * @throws ApiError
     */
    public companiesPaychexSyncRetrieve({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this paychex payroll sync.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<PaychexPayrollSyncDetail> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/paychex/sync/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PayrollSyncStatus
     * @throws ApiError
     */
    public companiesPaychexSyncTriggerCreate({
        parentCompany,
        requestBody,
    }: {
        parentCompany: string,
        requestBody: TriggerPayrollSyncRequest,
    }): CancelablePromise<PayrollSyncStatus> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/paychex/sync/trigger/',
            path: {
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedPersonioList
     * @throws ApiError
     */
    public companiesPersonioList({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<PaginatedPersonioList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/personio/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns Personio
     * @throws ApiError
     */
    public companiesPersonioCreate({
        parentCompany,
        requestBody,
    }: {
        parentCompany: string,
        requestBody: CreatePersonioAppRequest,
    }): CancelablePromise<Personio> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/personio/',
            path: {
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PayrollSyncStatus
     * @throws ApiError
     */
    public companiesPersonioCheckRetrieve({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<PayrollSyncStatus> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/personio/check/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns void
     * @throws ApiError
     */
    public companiesPersonioDeleteDestroy({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/companies/{parent_company}/personio/delete/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PersonioCompensationTypeSuccessResponse
     * @throws ApiError
     */
    public companiesPersonioListCompensationTypesRetrieve({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<PersonioCompensationTypeSuccessResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/personio/list-compensation-types/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedPersonioSyncList
     * @throws ApiError
     */
    public companiesPersonioSyncList({
        parentCompany,
        limit,
        offset,
    }: {
        parentCompany: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedPersonioSyncList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/personio/sync/',
            path: {
                'parent_company': parentCompany,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PayrollSyncStatus
     * @throws ApiError
     */
    public companiesPersonioSyncSalaryComponentsCreate({
        parentCompany,
    }: {
        parentCompany: string,
    }): CancelablePromise<PayrollSyncStatus> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/personio/sync-salary-components/',
            path: {
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PersonioSyncDetail
     * @throws ApiError
     */
    public companiesPersonioSyncRetrieve({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this personio sync.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<PersonioSyncDetail> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/personio/sync/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PayrollSyncStatus
     * @throws ApiError
     */
    public companiesPersonioSyncTriggerCreate({
        parentCompany,
        requestBody,
    }: {
        parentCompany: string,
        requestBody: TriggerPayrollSyncRequest,
    }): CancelablePromise<PayrollSyncStatus> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/personio/sync/trigger/',
            path: {
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
